import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../../Controllers/hooks";

import { Toast } from "primereact/toast";
import { Ripple } from "primereact/ripple";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";

import ErrorPage from "../../../../../Components/ErrorPage";
import PageLoader from "../../../../../Components/PageLoader";
import { goToTop } from "../../../../../Components/goToTop";
import { checkText } from "../../../../../Utils/Validator";
import { getLoginUserRoleName, UserRoleName } from "../../../../../Utils/LoginUserRoleName";

import { DutyHolderDetail } from "../../../../../Controllers/Types/DutyHolderDetails/DutyHolderDetailsTypes";

import { useSelector } from "react-redux";
import { RootState } from "../../../../../Controllers/Store";
import { hasPermission } from "../../../../../Controllers/Selectors/permissionsSelector";
import { getComplianceDeclarationFolderId } from "../../../../../Controllers/Thunks/BuildingInspectorThunks";
import { signDutyHolder } from "../../../../../Api/BuildingInspectorService/BuildingInspectorService";
import CustomTableLoader from "../../../../../Components/CustomTableLoader";

const ComplianceDeclaration: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const toast = useRef<Toast>(null);
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState<boolean>(false);
    const [loginUserRoleName, setLoginUserRoleName] = useState<UserRoleName | ''>('');
    const [dataState, setDataState] = useState<'Add' | 'Edit'>('Add');

    const projectData = location.state?.data;
    const folderId = Number(projectData.folder_id);

    const [cdProcessData, setCDProcessData] = useState<DutyHolderDetail[]>([]);
    const [cdProcessType, setCDProcessType] = useState<'Client' | 'P. Designer' | 'P. Contractor'>('Client');

    const [showCDProcessModal, setShowCDProcessModal] = useState<boolean>(false);

    const [clientData, setClientData] = useState<DutyHolderDetail | null>(null);
    const [principalDesignerData, setPrincipalDesignerData] = useState<DutyHolderDetail | null>(null);
    const [principalContractorData, setPrincipalContractorData] = useState<DutyHolderDetail | null>(null);

    const [isViewOnly, setIsViewOnly] = useState<boolean>(false);
    const [initialDataLoading, setInitialDataLoading] = useState<boolean>(true);
    const [signUpdateHasChanges, setSignUpdateHasChanges] = useState<boolean>(false);

    const [selectedDutyHoderData, setselectedDutyHoderData] = useState<DutyHolderDetail | null>(null);

    const [signBy, setSignBy] = useState<'Client' | 'P. Designer' | 'P. Contractor' | null>(null);
    const [showSignatureModal, setShowSignatureModal] = useState<boolean>(false);
    const [signature, setSignature] = useState<string>('');
    const [signatureError, setSignatureError] = useState<string>('');

    useEffect(() => {
        const fetchUserRole = async () => {
            const roleName = await getLoginUserRoleName();
            setLoginUserRoleName(roleName);
        };

        fetchUserRole();
    }, []);


    // PERMISION HANDLER
    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) =>
            hasPermission(state, screen, permission)
        );
    };

    const hasPermissionAddSignature = useCanEdit("Compliance Declaration", "add_signature");


    // for fetching data fro api tigger with state object actions
    useEffect(() => {
        complianceDeclarationDataGet();
    }, [folderId]);

    const complianceDeclarationDataGet = async () => {
        await dispatch(getComplianceDeclarationFolderId(folderId));
        setInitialDataLoading(false);
    }

    // this is get data from state list and set projectlist
    const complianceDeclarationList = useAppSelector((state) => state.complianceDeclarationDetails.complianceDeclarationList);
    const loadingComplianceDeclaration = useAppSelector((state) => state.complianceDeclarationDetails.loader);
    const errorComplianceDeclaration = useAppSelector((state) => state.complianceDeclarationDetails.errorDisplay);

    useEffect(() => {
        if (complianceDeclarationList && complianceDeclarationList?.length !== 0) {
            setCDProcessData(complianceDeclarationList);

            setClientData(complianceDeclarationList?.find(data => data?.type === 'Client') || null);
            setPrincipalDesignerData(complianceDeclarationList?.find(data => data?.type === 'P. Designer') || null);
            setPrincipalContractorData(complianceDeclarationList?.find(data => data?.type === 'P. Contractor') || null);
        } else {
            setCDProcessData([]);
            setClientData(null);
            setPrincipalDesignerData(null);
            setPrincipalContractorData(null);
        }
    }, [complianceDeclarationList]);

    const handleChangeCDProcessType = (type: 'Client' | 'P. Designer' | 'P. Contractor') => {
        setCDProcessType(type);
    }

    const handleCloseCDProcessModal = () => {
        setShowCDProcessModal(false);
    }

    const handleShowCDProcess = () => {
        setShowCDProcessModal(true);
    }

    /* Modal header */
    const cdProcessModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                CD Process
            </h5>

            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleCloseCDProcessModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );


    /* ********************************** Sign *********************************** */
    const handleCloseSignatureModal = () => {
        setShowSignatureModal(false);
        setSignature('');
        setSignatureError('');
    }

    const handleTakeSignature = (state: 'Add' | 'Edit', by: 'Client' | 'P. Designer' | 'P. Contractor', data: DutyHolderDetail) => {
        setDataState(state);
        setShowSignatureModal(true);
        setSignature('');
        setselectedDutyHoderData(data);
        setSignBy(by);

        if (state === 'Edit') {
            setSignature(data?.sign || '')
        } else {
            setSignature('');
        }
    }

    // remove conditions
    function eligibleSign(data: DutyHolderDetail): boolean {
        const isSignatureValid = (localStorage.getItem('login_email') === data.email);
        return isSignatureValid;
    }

    function checkSignCredentials(): boolean {
        const isSignatureValid = checkText(signature, true, setSignatureError);

        return isSignatureValid;
    }

    function showAPIErrorSign(keyName: string, error: string): void {
        if (keyName === "sign") {
            setSignatureError(error);
        }
    }

    useEffect(() => {
        checkText(signature, false, setSignatureError)
    }, [signature]);

    useEffect(() => {
        if (selectedDutyHoderData) {
            if (signature !== null && signature !== selectedDutyHoderData?.sign) {
                setSignUpdateHasChanges(true);
            } else {
                setSignUpdateHasChanges(false);
            }
        }
    }, [selectedDutyHoderData, signature]);

    // database signature functions as well
    const handleSign = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkSignCredentials()) {
            setLoading(true);
            const data = await signDutyHolder(selectedDutyHoderData?.id!, signBy!, signature);

            setLoading(false);
            if (data.statusCode === 200) {
                complianceDeclarationDataGet();
                handleCloseSignatureModal();

                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Success",
                        detail: `Signed successfully.`,
                        life: 3000,
                    });
                }
            } else if (data.statusCode === 422) {
                // database error
                Object.entries(data.errors ?? []).map(([key, value], index) =>
                    showAPIErrorSign(key, value[0])
                );
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: `There was an error signing.`,
                        life: 3000,
                    });
                }
            }
        }
    }

    const signatureModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                {dataState} Sign
            </h5>

            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleCloseSignatureModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    )

    const signatureModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Cancel"
                className="custom_btn secondary"
                onClick={handleCloseSignatureModal}
            />
            <Button
                loading={loading}
                label={`${loading ? 'Processing' : dataState === 'Add' ? 'Submit' : 'Update'}`}
                className="custom_btn primary"
                onClick={handleSign}
                disabled={dataState === 'Edit' && !signUpdateHasChanges}
            />
        </div>
    )
    /* *************************************************************************** */


    /* **************************** Common functions ***************************** */
    const goBack = () => {
        navigate(-1);
        goToTop();
    };
    /* *************************************************************************** */

    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content layout_padding show_side_bar">
                {loadingComplianceDeclaration && initialDataLoading ? (
                    <PageLoader />
                ) : errorComplianceDeclaration ? (
                    <ErrorPage errorMessage={errorComplianceDeclaration} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="page_breadcrumb_area">
                            <ul className="page_breadcrumb">
                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                                    {projectData.job_number} : {projectData.address}
                                </li>
                                <li className="page_breadcrumb_item has_child">
                                    {projectData.folder}
                                </li>
                                <li className="page_breadcrumb_item active">
                                    {projectData.feature_name}
                                </li>
                            </ul>

                            <button className="back_btn p-ripple" onClick={goBack}>
                                <i className="bi bi-arrow-left me-2"></i>
                                Back
                                <Ripple />
                            </button>
                        </div>

                        <div className="page_header_area mt-3">
                            <div className="page_action_area">
                                <button
                                    className={`add_data_link_btn ${cdProcessData && cdProcessData?.length !== 0 ? 'fill' : ''} p-ripple`}
                                    onClick={handleShowCDProcess}
                                >
                                    <i className="bi bi-person-gear"></i>
                                    <span>CD Process</span>
                                    <Ripple />
                                </button>
                            </div>
                        </div>

                        <div className="page_main_content_section">
                            {loadingComplianceDeclaration && (
                                <CustomTableLoader />
                            )}

                            <div className="commencement_area">
                                <p>
                                    Please note, this declaration should ideally be signed once the works are nearing the end of completion / complete on-site.
                                </p>

                                <p>
                                    If this declaration is signed early, the Lead Surveyor may request for it to be re-signed.
                                </p>

                                <p>
                                    Please click the 'Initiate the process' button below to obtain digital signatures. An email will be sent to each dutyholder, asking them to confirm.
                                </p>

                                <p>
                                    Please check the dutyholder details below before doing so. If these are incorrect, you are able to update their details in the "Dutyholder Details" section above
                                </p>

                                <p>
                                    Please note: You can not initiate the Compliance Declaration process until we have the details for all of the dutyholders.
                                </p>

                                <div className="commencement_sub_area text-capitalize mt-4">
                                    <p>
                                        <span className="text_bold">Client:</span> {clientData?.client_name || '-----'}
                                    </p>

                                    <p>
                                        <span className="text_bold">Principal Designer:</span> {principalDesignerData?.name || '-----'}
                                    </p>

                                    <p className="mb-0">
                                        <span className="text_bold">Principal Contractor:</span> {principalContractorData?.name || '-----'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>


            {/* CD Process modal */}
            <Dialog
                visible={showCDProcessModal}
                header={cdProcessModalHeader}
                headerClassName="custom_modal_header"
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseCDProcessModal}
                dismissableMask
                contentClassName="py-0"
            >
                <div className="custom_modal_body">
                    <div className="px-1 px-sm-3 py-sm-1 w-100">
                        {/* CD process type */}
                        <div className="toggle_option_btn_group sticky">
                            <button
                                role="tab"
                                type="button"
                                className={`toggle_option_btn ${cdProcessType === 'Client' ? 'active' : ''} p-ripple`}
                                onClick={() => handleChangeCDProcessType('Client')}
                            >
                                Client
                                <Ripple pt={{ root: { style: { background: 'rgba(164, 96, 253, 0.2)' } } }} />
                            </button>
                            <button
                                role="tab"
                                type="button"
                                className={`toggle_option_btn center ${cdProcessType === 'P. Designer' ? 'active' : ''} p-ripple`}
                                onClick={() => handleChangeCDProcessType('P. Designer')}
                            >
                                P. Designer
                                <Ripple pt={{ root: { style: { background: 'rgba(164, 96, 253, 0.2)' } } }} />
                            </button>
                            <button
                                role="tab"
                                type="button"
                                className={`toggle_option_btn ${cdProcessType === 'P. Contractor' ? 'active' : ''} p-ripple`}
                                onClick={() => handleChangeCDProcessType('P. Contractor')}
                            >
                                P. Contractor
                                <Ripple pt={{ root: { style: { background: 'rgba(164, 96, 253, 0.2)' } } }} />
                            </button>
                        </div>

                        <div className="row">
                            <div className="col-12">
                                {cdProcessType === 'Client' && (
                                    <React.Fragment>
                                        {clientData && clientData !== null ? (
                                            <div className="detail_container">
                                                <div className="detail_content_group">
                                                    <h6>Client :</h6>
                                                    <p>
                                                        {clientData?.client_name ? (
                                                            <span>{clientData?.client_name}</span>
                                                        ) : (
                                                            <span className="text_italic">N/A</span>
                                                        )}
                                                    </p>
                                                </div>

                                                <div className="detail_content_group">
                                                    <h6>Address :</h6>
                                                    <p>
                                                        {clientData?.address ? (
                                                            <span>{clientData?.address}</span>
                                                        ) : (
                                                            <span className="text_italic">N/A</span>
                                                        )}
                                                    </p>
                                                </div>

                                                <div className="detail_content_group">
                                                    <h6>Email :</h6>
                                                    <p>
                                                        {clientData?.email ? (
                                                            <span>{clientData?.email}</span>
                                                        ) : (
                                                            <span className="text_italic">N/A</span>
                                                        )}
                                                    </p>
                                                </div>

                                                <div className="detail_content_group">
                                                    <h6>Telephone :</h6>
                                                    <p>
                                                        {clientData?.telephone ? (
                                                            <span>{clientData?.telephone}</span>
                                                        ) : (
                                                            <span className="text_italic">N/A</span>
                                                        )}
                                                    </p>
                                                </div>

                                                <div className="detail_content_group no_divider">
                                                    <h6>Work Complete Date :</h6>
                                                    <p>
                                                        {clientData?.work_complete_date ? (
                                                            <span>{clientData?.work_complete_date}</span>
                                                        ) : (
                                                            <span className="text_italic">N/A</span>
                                                        )}
                                                    </p>
                                                </div>

                                                <div className="detail_content_group with_bg">
                                                    <h6>I declare that :</h6>
                                                    <p>
                                                        {clientData?.declare_that ? (
                                                            <span>{clientData?.declare_that}</span>
                                                        ) : (
                                                            <span className="text_italic">N/A</span>
                                                        )}
                                                    </p>
                                                </div>

                                                {clientData?.sign_report === 1 ? (
                                                    <div className="sv_report_footer mb-3">
                                                        <div className="sv_report_footer_item">
                                                            <h3>
                                                                {clientData?.sign}

                                                                {eligibleSign(clientData) && hasPermissionAddSignature && (
                                                                    <button className="is_btn sign_edit_btn ms-1"
                                                                        onClick={() => handleTakeSignature('Edit', 'Client', clientData)}>
                                                                        <i className="bi bi-pencil-square"></i>
                                                                    </button>
                                                                )}
                                                            </h3>

                                                            <h5>Prepared by</h5>
                                                            <h6>{clientData?.sign_date}</h6>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <React.Fragment>
                                                        {isViewOnly ? (
                                                            <div className="no_sign_area">
                                                                <h6>
                                                                    <i className="bi bi-exclamation-circle me-1"></i>
                                                                    <h6>Have not signed!</h6>
                                                                </h6>
                                                            </div>
                                                        ) : (
                                                            <React.Fragment>
                                                                {hasPermissionAddSignature && (
                                                                    <div className="detail_content_footer">
                                                                        <button
                                                                            disabled={!eligibleSign(clientData)}
                                                                            className={`add_data_link_btn radiused custom_width p-ripple`}
                                                                            onClick={() => handleTakeSignature('Add', 'Client', clientData)}
                                                                        >
                                                                            <i className="bi bi-pen"></i>
                                                                            <span>Sign</span>
                                                                            <Ripple />
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </React.Fragment>
                                                        )}
                                                    </React.Fragment>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="empty_detail_container">
                                                <img src="/assets/images/no_data/no_data.svg" alt="No data" />
                                                <h6>
                                                    <i className="bi bi-exclamation-circle me-1"></i>
                                                    No data found for client!
                                                </h6>
                                            </div>
                                        )}
                                    </React.Fragment>
                                )}

                                {cdProcessType === 'P. Designer' && (
                                    <React.Fragment>
                                        {principalDesignerData && principalDesignerData !== null ? (
                                            <div className="detail_container">
                                                <div className="detail_content_group">
                                                    <h6>Principal Designer :</h6>
                                                    <p>
                                                        {principalDesignerData?.principal_designer ? (
                                                            <span>{principalDesignerData?.principal_designer}</span>
                                                        ) : (
                                                            <span className="text_italic">N/A</span>
                                                        )}
                                                    </p>
                                                </div>

                                                <div className="detail_content_group">
                                                    <h6>Name :</h6>
                                                    <p>
                                                        {principalDesignerData?.name ? (
                                                            <span>{principalDesignerData?.name}</span>
                                                        ) : (
                                                            <span className="text_italic">N/A</span>
                                                        )}
                                                    </p>
                                                </div>

                                                <div className="detail_content_group">
                                                    <h6>Address :</h6>
                                                    <p>
                                                        {principalDesignerData?.address ? (
                                                            <span>{principalDesignerData?.address}</span>
                                                        ) : (
                                                            <span className="text_italic">N/A</span>
                                                        )}
                                                    </p>
                                                </div>

                                                <div className="detail_content_group">
                                                    <h6>Email :</h6>
                                                    <p>
                                                        {principalDesignerData?.email ? (
                                                            <span>{principalDesignerData?.email}</span>
                                                        ) : (
                                                            <span className="text_italic">N/A</span>
                                                        )}
                                                    </p>
                                                </div>

                                                <div className="detail_content_group">
                                                    <h6>Telephone :</h6>
                                                    <p>
                                                        {principalDesignerData?.telephone ? (
                                                            <span>{principalDesignerData?.telephone}</span>
                                                        ) : (
                                                            <span className="text_italic">N/A</span>
                                                        )}
                                                    </p>
                                                </div>

                                                <div className="detail_content_group no_divider">
                                                    <h6>Appointment Date :</h6>
                                                    <p>
                                                        {principalDesignerData?.appointment_date ? (
                                                            <span>{principalDesignerData?.appointment_date}</span>
                                                        ) : (
                                                            <span className="text_italic">N/A</span>
                                                        )}
                                                    </p>
                                                </div>

                                                <div className="detail_content_group with_bg">
                                                    <h6>I declare that :</h6>
                                                    <p>
                                                        {principalDesignerData?.declare_that ? (
                                                            <span>{principalDesignerData?.declare_that}</span>
                                                        ) : (
                                                            <span className="text_italic">N/A</span>
                                                        )}
                                                    </p>
                                                </div>

                                                {principalDesignerData?.sign_report === 1 ? (
                                                    <div className="sv_report_footer mb-3">
                                                        <div className="sv_report_footer_item">
                                                            <h3>
                                                                {principalDesignerData?.sign}

                                                                {eligibleSign(principalDesignerData) && hasPermissionAddSignature && (
                                                                    <button className="is_btn sign_edit_btn ms-1"
                                                                        onClick={() => handleTakeSignature('Edit', 'P. Designer', principalDesignerData)}>
                                                                        <i className="bi bi-pencil-square"></i>
                                                                    </button>
                                                                )}
                                                            </h3>

                                                            <h5>Prepared by</h5>
                                                            <h6>{principalDesignerData?.sign_date}</h6>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <React.Fragment>
                                                        {isViewOnly ? (
                                                            <div className="no_sign_area">
                                                                <h6>
                                                                    <i className="bi bi-exclamation-circle me-1"></i>
                                                                    Have not signed!
                                                                </h6>
                                                            </div>
                                                        ) : (
                                                            <React.Fragment>
                                                                {hasPermissionAddSignature && (
                                                                    <div className="detail_content_footer">
                                                                        <button
                                                                            disabled={!eligibleSign(principalDesignerData)}
                                                                            className={`add_data_link_btn radiused custom_width p-ripple`}
                                                                            onClick={() => handleTakeSignature('Add', 'P. Designer', principalDesignerData)}
                                                                        >
                                                                            <i className="bi bi-pen"></i>
                                                                            <span>Sign</span>
                                                                            <Ripple />
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </React.Fragment>
                                                        )}
                                                    </React.Fragment>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="empty_detail_container">
                                                <img src="/assets/images/no_data/no_data.svg" alt="No data" />
                                                <h6>
                                                    <i className="bi bi-exclamation-circle me-1"></i>
                                                    No data found for principal designer!
                                                </h6>
                                            </div>
                                        )}
                                    </React.Fragment>
                                )}

                                {cdProcessType === 'P. Contractor' && (
                                    <React.Fragment>
                                        {principalContractorData && principalContractorData !== null ? (
                                            <div className="detail_container">
                                                <div className="detail_content_group">
                                                    <h6>Principal Contractor :</h6>
                                                    <p>
                                                        {principalContractorData?.principal_designer ? (
                                                            <span>{principalContractorData?.principal_designer}</span>
                                                        ) : (
                                                            <span className="text_italic">N/A</span>
                                                        )}
                                                    </p>
                                                </div>

                                                <div className="detail_content_group">
                                                    <h6>Name :</h6>
                                                    <p>
                                                        {principalContractorData?.name ? (
                                                            <span>{principalContractorData?.name}</span>
                                                        ) : (
                                                            <span className="text_italic">N/A</span>
                                                        )}
                                                    </p>
                                                </div>

                                                <div className="detail_content_group">
                                                    <h6>Address :</h6>
                                                    <p>
                                                        {principalContractorData?.address ? (
                                                            <span>{principalContractorData?.address}</span>
                                                        ) : (
                                                            <span className="text_italic">N/A</span>
                                                        )}
                                                    </p>
                                                </div>

                                                <div className="detail_content_group">
                                                    <h6>Email :</h6>
                                                    <p>
                                                        {principalContractorData?.email ? (
                                                            <span>{principalContractorData?.email}</span>
                                                        ) : (
                                                            <span className="text_italic">N/A</span>
                                                        )}
                                                    </p>
                                                </div>

                                                <div className="detail_content_group">
                                                    <h6>Telephone :</h6>
                                                    <p>
                                                        {principalContractorData?.telephone ? (
                                                            <span>{principalContractorData?.telephone}</span>
                                                        ) : (
                                                            <span className="text_italic">N/A</span>
                                                        )}
                                                    </p>
                                                </div>

                                                <div className="detail_content_group no_divider">
                                                    <h6>Appointment Date :</h6>
                                                    <p>
                                                        {principalContractorData?.appointment_date ? (
                                                            <span>{principalContractorData?.appointment_date}</span>
                                                        ) : (
                                                            <span className="text_italic">N/A</span>
                                                        )}
                                                    </p>
                                                </div>

                                                <div className="detail_content_group with_bg">
                                                    <h6>I declare that :</h6>
                                                    <p>
                                                        {principalContractorData?.declare_that ? (
                                                            <span>{principalContractorData?.declare_that}</span>
                                                        ) : (
                                                            <span className="text_italic">N/A</span>
                                                        )}
                                                    </p>
                                                </div>

                                                {principalContractorData?.sign_report === 1 ? (
                                                    <div className="sv_report_footer mb-3">
                                                        <div className="sv_report_footer_item">
                                                            <h3>
                                                                {principalContractorData?.sign}

                                                                {eligibleSign(principalContractorData) && hasPermissionAddSignature && (
                                                                    <button className="is_btn sign_edit_btn ms-1"
                                                                        onClick={() => handleTakeSignature('Edit', 'P. Contractor', principalContractorData)}>
                                                                        <i className="bi bi-pencil-square"></i>
                                                                    </button>
                                                                )}
                                                            </h3>

                                                            <h5>Prepared by</h5>
                                                            <h6>{principalContractorData?.sign_date}</h6>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <React.Fragment>
                                                        {isViewOnly ? (
                                                            <div className="no_sign_area">
                                                                <h6>
                                                                    <i className="bi bi-exclamation-circle me-1"></i>
                                                                    Have not signed!
                                                                </h6>
                                                            </div>
                                                        ) : (
                                                            <React.Fragment>
                                                                {hasPermissionAddSignature && (
                                                                    <div className="detail_content_footer">
                                                                        <button
                                                                            disabled={!eligibleSign(principalContractorData)}
                                                                            className={`add_data_link_btn radiused custom_width p-ripple`}
                                                                            onClick={() => handleTakeSignature('Add', 'P. Contractor', principalContractorData)}
                                                                        >
                                                                            <i className="bi bi-pen"></i>
                                                                            <span>Sign</span>
                                                                            <Ripple />
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </React.Fragment>
                                                        )}
                                                    </React.Fragment>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="empty_detail_container">
                                                <img src="/assets/images/no_data/no_data.svg" alt="No data" />
                                                <h6>
                                                    <i className="bi bi-exclamation-circle me-1"></i>
                                                    No data found for principal contractor!
                                                </h6>
                                            </div>
                                        )}
                                    </React.Fragment>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Signature modal */}
            <Dialog
                header={signatureModalHeader}
                footer={signatureModalFooter}
                headerClassName="custom_modal_header"
                visible={showSignatureModal}
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseSignatureModal}
                dismissableMask
            >
                <div className="custom_modal_body">
                    <div className="p-1 p-sm-3 pb-sm-0 w-100">
                        <div className="row">
                            <div className="col-12">
                                <div className="page_form_group">
                                    <label htmlFor="signature" className="custom_form_label is_required">Signature</label>

                                    <InputText
                                        id='signature'
                                        value={signature}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSignature(e.target.value)}
                                        className="custom_form_input signature_input"
                                        placeholder='Sign'
                                        autoFocus
                                        type='text'
                                    />

                                    {signatureError && (<small className="form_error_msg">{signatureError}</small>)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
            {/*  */}
        </React.Fragment>
    )
}

export default ComplianceDeclaration;